(function (root, factory) {
  const availability = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = availability;
  }

  if (root) {
    root.RefurbishedAvailability = availability;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  function create(catalogue) {
    const showFullyUnavailableSwatches =
      catalogue.showFullyUnavailableSwatches !== false;
    const optionIds = catalogue.options.map((option) => option.id);
    const valueOrder = Object.fromEntries(
      catalogue.options.map((option) => [
        option.id,
        new Map(option.values.map((value, index) => [value.id, index])),
      ]),
    );
    const variants = catalogue.variants.map((variant) => ({
      ...variant,
      selection: selectionForVariant(catalogue.options, variant),
    }));
    const localVariants = variants.filter(
      (variant) =>
        isAtFixedLocation(variant, catalogue.fixed) &&
        optionIds.every((optionId) => variant.selection[optionId] !== null),
    );
    const availableVariants = localVariants
      .filter(
        (variant) => variant.available,
      )
      .sort((left, right) =>
        compareSelections(left.selection, right.selection, optionIds, valueOrder),
      );

    function buildStates(selection) {
      return Object.fromEntries(
        catalogue.options.map((option) => [
          option.id,
          Object.fromEntries(
            option.values.map((value) => {
              if (availableVariants.length === 0) {
                return [value.id, 'struck'];
              }

              const existsInStock = availableVariants.some(
                (variant) => variant.selection[option.id] === value.id,
              );

              if (!existsInStock) {
                return [
                  value.id,
                  showFullyUnavailableSwatches ? 'struck' : 'hidden',
                ];
              }

              const candidateSelection = {
                ...selection,
                [option.id]: value.id,
              };
              const exactVariant = availableVariants.find((variant) =>
                selectionsEqual(
                  variant.selection,
                  candidateSelection,
                  optionIds,
                ),
              );

              return [value.id, exactVariant ? 'ready' : 'struck'];
            }),
          ),
        ]),
      );
    }

    function resolution(selection, variant) {
      return {
        selection: { ...selection },
        variantId: variant ? variant.id : null,
        canAddToCart: Boolean(variant && variant.available),
        allSoldOut: availableVariants.length === 0,
        states: buildStates(selection),
      };
    }

    function initialise(currentSelection) {
      const currentVariant = localVariants.find((candidate) =>
        selectionsEqual(candidate.selection, currentSelection, optionIds),
      );
      const variant = availableVariants.length
        ? availableVariants.find((candidate) =>
          selectionsEqual(candidate.selection, currentSelection, optionIds),
        ) || availableVariants[0]
        : currentVariant;

      const selection =
        variant && variant.available ? variant.selection : currentSelection;
      return resolution(selection, variant);
    }

    function select(currentSelection, clickedOption, clickedValue) {
      const requestedSelection = {
        ...currentSelection,
        [clickedOption]: clickedValue,
      };
      const clickedValueIsAvailable = availableVariants.some(
        (variant) => variant.selection[clickedOption] === clickedValue,
      );
      const soldOutVariant = localVariants.find((variant) =>
        selectionsEqual(variant.selection, requestedSelection, optionIds),
      );

      if (availableVariants.length > 0 && !clickedValueIsAvailable) {
        return resolution(requestedSelection, soldOutVariant);
      }

      const exactVariant = availableVariants.find((variant) =>
        selectionsEqual(variant.selection, requestedSelection, optionIds),
      );
      const fallbackVariant = availableVariants
        .filter((variant) => variant.selection[clickedOption] === clickedValue)
        .sort((left, right) => {
          const leftChanges = changedOptionIndexes(
            left.selection,
            currentSelection,
            optionIds,
            clickedOption,
          );
          const rightChanges = changedOptionIndexes(
            right.selection,
            currentSelection,
            optionIds,
            clickedOption,
          );

          if (leftChanges.length !== rightChanges.length) {
            return leftChanges.length - rightChanges.length;
          }

          for (let index = 0; index < leftChanges.length; index += 1) {
            if (leftChanges[index] !== rightChanges[index]) {
              return leftChanges[index] - rightChanges[index];
            }
          }

          return compareSelections(
            left.selection,
            right.selection,
            optionIds,
            valueOrder,
          );
        })[0];
      const variant =
        availableVariants.length === 0
          ? soldOutVariant
          : exactVariant || fallbackVariant;
      const selection =
        variant && variant.available ? variant.selection : requestedSelection;

      return resolution(selection, variant);
    }

    return { initialise, select };
  }

  function changedOptionIndexes(
    selection,
    currentSelection,
    optionIds,
    clickedOption,
  ) {
    return optionIds
      .map((optionId, index) => ({ optionId, index }))
      .filter(
        ({ optionId }) =>
          optionId !== clickedOption &&
          selection[optionId] !== currentSelection[optionId],
      )
      .map(({ index }) => index);
  }

  function compareSelections(left, right, optionIds, valueOrder) {
    for (const optionId of optionIds) {
      const difference =
        valueOrder[optionId].get(left[optionId]) -
        valueOrder[optionId].get(right[optionId]);

      if (difference !== 0) {
        return difference;
      }
    }

    return 0;
  }

  function selectionForVariant(options, variant) {
    return Object.fromEntries(
      options.map((option) => {
        const value = option.values.find((candidate) =>
          Object.entries(candidate.match).every(
            ([key, expected]) => variant.attributes[key] === expected,
          ),
        );

        return [option.id, value ? value.id : null];
      }),
    );
  }

  function isAtFixedLocation(variant, fixed) {
    return Object.entries(fixed).every(
      ([key, expected]) =>
        String(variant.attributes[key]).trim().toLowerCase() ===
        String(expected).trim().toLowerCase(),
    );
  }

  function selectionsEqual(left, right, optionIds) {
    return optionIds.every((optionId) => left[optionId] === right[optionId]);
  }

  return { create };
});
